import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../lib/supabase';

const { 
  FiCalendar, FiClock, FiPlus, FiEdit, FiTrash2, FiCheck, FiX, 
  FiHome, FiHeart, FiMapPin, FiFilter, FiSearch, FiChevronLeft, 
  FiChevronRight, FiArrowLeft,FiPhone,FiMail  
} = FiIcons;

const Scheduling = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [userRole, setUserRole] = useState(null); // State to hold the user's role

  // ---------------------------
  // ✅ Updated: Fetch appointments based on user role
  // ---------------------------
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchRoleAndAppointments = async () => {
      try {
        // 1. Fetch user role from the 'profiles' table, similar to the Dashboard
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Error fetching user role:', profileError.message);
        }
        
        const role = profile?.role || 'user';
        setUserRole(role);

        // 2. Build the appointments query based on the fetched role
        let query = supabase
          .from('appointments')
          .select('*')
          .order('start_time', { ascending: true });

        // If the user is NOT an admin, filter appointments by their user_id
        if (role !== 'admin') {
          query = query.eq('user_id', user.id);
        }
        
        // For admin, the query remains unfiltered, fetching all appointments.

        // 3. Execute the query
        const { data, error } = await query;

        if (error) throw error;

        const appointmentsWithDate = data.map(apt => ({
          ...apt,
          date: new Date(apt.start_time) // convert timestamptz to Date
        }));

        setAppointments(appointmentsWithDate);
        setFilteredAppointments(appointmentsWithDate);
      } catch (err) {
        console.error('Error fetching data:', err.message);
      }
    };

    fetchRoleAndAppointments();
  }, [user, navigate]);

  // ---------------------------
  // Filter & search appointments (No changes needed here)
  // ---------------------------
  useEffect(() => {
    let filtered = appointments;

    if (filter !== 'all') {
      filtered = filtered.filter(apt => apt.service_type === filter);
    }

    if (searchTerm) {
      filtered = filtered.filter(apt =>
        apt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (apt.provider_id && apt.provider_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (apt.description && apt.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredAppointments(filtered);
  }, [appointments, filter, searchTerm]);

  // ---------------------------
  // Helper functions (No changes needed here)
  // ---------------------------
  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'property-care': return FiHome;
      case 'parental-care': return FiHeart;
      case 'travel-services': return FiMapPin;
      default: return FiCalendar;
    }
  };

  const getServiceColor = (serviceType) => {
    switch (serviceType) {
      case 'property-care': return 'blue';
      case 'parental-care': return 'red';
      case 'travel-services': return 'green';
      default: return 'gray';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day));

    return days;
  };

  const getAppointmentsForDate = (date) => {
    return filteredAppointments.filter(apt => apt.date.toDateString() === date.toDateString());
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // ---------------------------
  // Render Calendar & Appointments (No changes needed here)
  // ---------------------------
  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate);
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <div className="flex items-center space-x-2">
            <button onClick={() => navigateMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <SafeIcon icon={FiChevronLeft} className="text-lg" />
            </button>
            <button onClick={() => setCurrentDate(new Date())} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
              Today
            </button>
            <button onClick={() => navigateMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <SafeIcon icon={FiChevronRight} className="text-lg" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(day => (
            <div key={day} className="p-1 sm:p-2 text-center text-xs sm:text-sm font-medium text-gray-500 truncate">{day}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            if (!day) return <div key={index} className="h-14 sm:h-20 md:h-24 p-1"></div>;

            const dayAppointments = getAppointmentsForDate(day);
            const isToday = day.toDateString() === new Date().toDateString();
            const isSelected = day.toDateString() === selectedDate.toDateString();

            return (
              <div
                key={index}
                onClick={() => setSelectedDate(day)}
                className={`h-14 sm:h-20 md:h-24 p-1 border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors overflow-hidden ${
                  isToday ? 'bg-blue-50 border-blue-200' : ''
                } ${isSelected ? 'bg-primary-50 border-primary-200' : ''}`}
              >
                <div className={`text-xs sm:text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                  {day.getDate()}
                </div>
                <div className="space-y-1 hidden sm:block">
                  {dayAppointments.slice(0, 2).map(apt => (
                    <div key={apt.id} className={`text-[10px] sm:text-xs px-1 py-0.5 rounded truncate bg-${getServiceColor(apt.service_type)}-100 text-${getServiceColor(apt.service_type)}-700`}>
                      {apt.title}
                    </div>
                  ))}
                  {dayAppointments.length > 2 && <div className="text-[10px] sm:text-xs text-gray-500">+{dayAppointments.length - 2} more</div>}
                </div>
                {dayAppointments.length > 0 && (
                  <div className="block sm:hidden text-center">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-600"></span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAppointmentsList = () => {
    const selectedDateAppointments = getAppointmentsForDate(selectedDate);

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">{formatDate(selectedDate)}</h3>
          <Link
            to="/appointments"
            className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} className="text-sm" />
            <span>New Appointment</span>
          </Link>
        </div>

        {selectedDateAppointments.length === 0 ? (
          <div className="text-center py-8">
            <SafeIcon icon={FiCalendar} className="text-4xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No appointments scheduled for this date</p>
          </div>
        ) : (
          <div className="space-y-4">
            {selectedDateAppointments.map(appointment => (
              <motion.div key={appointment.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`w-10 h-10 bg-${getServiceColor(appointment.service_type)}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <SafeIcon icon={getServiceIcon(appointment.service_type)} className={`text-lg text-${getServiceColor(appointment.service_type)}-600`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-lg font-semibold text-gray-900">{appointment.title}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                          {appointment.status}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiClock} className="text-xs" />
                          <span>{formatTime(appointment.date)} ({appointment.duration} min)</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiMapPin} className="text-xs" />
                          <span>{appointment.provider_id || 'N/A'}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">
  {appointment.description}
</p>

<div className="space-y-2">

  {appointment.location && (
    <div className="flex items-center text-sm text-gray-600">
      <SafeIcon
        icon={FiMapPin}
        className="mr-2 text-primary-500"
      />
      <span>{appointment.location}</span>
    </div>
  )}

  {appointment.phone && (
    <div className="flex items-center text-sm">
      <SafeIcon
        icon={FiPhone}
        className="mr-2 text-green-600"
      />

      <a
        href={`tel:${appointment.phone}`}
        className="text-green-700 hover:text-green-900 hover:underline font-medium"
      >
        {appointment.phone}
      </a>
    </div>
  )}

  {appointment.email && (
    <div className="flex items-center text-sm">
      <SafeIcon
        icon={FiMail}
        className="mr-2 text-blue-600"
      />

      <a
        href={`mailto:${appointment.email}`}
        className="text-blue-700 hover:text-blue-900 hover:underline font-medium"
      >
        {appointment.email}
      </a>
    </div>
  )}

</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {/*<button className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                      <SafeIcon icon={FiEdit} className="text-sm" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <SafeIcon icon={FiTrash2} className="text-sm" />
                    </button>*/}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4">
            <SafeIcon icon={FiArrowLeft} className="text-lg" />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Service Scheduling</h1>
              <p className="text-gray-600">
                {userRole === 'admin' ? "Viewing all appointments across the platform." : "Manage your appointments and service schedules"}
              </p>
            </div>
            <Link to="/request-service" className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2">
              <SafeIcon icon={FiPlus} className="text-sm" />
              <span>Schedule Service</span>
            </Link>
          </div>
        </div>

        {/* Filters (No changes needed here) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search appointments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-64"
                />
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Services</option>
                <option value="property-care">Property Care</option>
                <option value="parental-care">Parental Care</option>
                <option value="travel-services">Travel Services</option>
              </select>
            </div>
            {/*<div className="flex items-center space-x-2">
              <button onClick={() => setViewMode('month')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'month' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Month</button>
              <button onClick={() => setViewMode('week')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'week' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Week</button>
              <button onClick={() => setViewMode('list')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>List</button>
            </div>*/}
          </div>
        </div>

        {/* Main Content (No changes needed here) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">{renderMonthView()}</div>
          <div>{renderAppointmentsList()}</div>
        </div>
      </div>
    </div>
  );
};

export default Scheduling;